import React, { useEffect, useRef, useState, useCallback } from "react";
import "./welcome.css";
import Logo from "../../Assets/logo.webp";

const Welcome = () => {
  const [phase, setPhase] = useState("splash"); // "splash" | "transitioning" | "done"
  const splashRef = useRef(null);
  const siteRef = useRef(null);
  const startYRef = useRef(null);
  const isDraggingRef = useRef(false);
  const hasTriggeredRef = useRef(false);

  // On mount: check if already visited this session
  useEffect(() => {
    const alreadySeen = sessionStorage.getItem("shree_welcome_seen");
    if (alreadySeen) {
      setPhase("done");
    }
  }, []);

  const triggerTransition = useCallback(() => {
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;
    setPhase("transitioning");
    sessionStorage.setItem("shree_welcome_seen", "true");

    // After animation completes, hide splash entirely
    setTimeout(() => setPhase("done"), 1000);
  }, []);

  // ── SCROLL HANDLER ──
  useEffect(() => {
    if (phase !== "splash") return;

    const handleScroll = () => {
      if (window.scrollY > 30) triggerTransition();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [phase, triggerTransition]);

  // ── TOUCH / SWIPE UP HANDLER ──
  useEffect(() => {
    if (phase !== "splash") return;

    const handleTouchStart = (e) => {
      startYRef.current = e.touches[0].clientY;
      isDraggingRef.current = true;
    };

    const handleTouchMove = (e) => {
      if (!isDraggingRef.current || startYRef.current === null) return;
      const deltaY = startYRef.current - e.touches[0].clientY;
      if (deltaY > 50) triggerTransition();
    };

    const handleTouchEnd = () => {
      isDraggingRef.current = false;
      startYRef.current = null;
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [phase, triggerTransition]);

  // ── CLICK HANDLER (tap to enter) ──
  const handleClick = () => {
    if (phase === "splash") triggerTransition();
  };

  if (phase === "done") return null;

  const isTransitioning = phase === "transitioning";

  return (
    <>
      {/* ── SPLASH OVERLAY ── */}
      <div
        ref={splashRef}
        className={`welcome-container ${isTransitioning ? "slide-up" : ""}`}
        onClick={handleClick}
      >
        {/* Background photo */}
        <div className="welcome-bg" />

        {/* Warm gradient overlay */}
        <div className="welcome-overlay" />

        {/* Shimmer light sweep */}
        <div className="welcome-shimmer" />

        {/* Floating bokeh orbs */}
        <div className="bokeh-wrap">
          {[...Array(8)].map((_, i) => (
            <span key={i} className={`bokeh bokeh-${i + 1}`} />
          ))}
        </div>

        {/* Content */}
        <div className="welcome-content">
          <p className="welcome-eyebrow">Est. 2017 · Surat, Gujarat</p>

          <div className="welcome-logo-wrap">
            <img
              src= {Logo}
              alt="Shree Logo"
              className="welcome-logo-img"
            />
          </div>

          <h1 className="welcome-title">Shree Photos Films</h1>
          <p className="tagline">Capturing Moments That Last Forever</p>

          <div className="divider-ornament">
            <span />
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" fill="#c9a84c"/>
            </svg>
            <span />
          </div>

          <div className="scroll-hint">
            <span className="scroll-text">Scroll or tap to enter</span>
            <div className="scroll-chevrons">
              <div className="chev chev-1" />
              <div className="chev chev-2" />
              <div className="chev chev-3" />
            </div>
          </div>
        </div>

        {/* Corner decorations */}
        <div className="corner corner-tl" />
        <div className="corner corner-tr" />
        <div className="corner corner-bl" />
        <div className="corner corner-br" />
      </div>

      {/* ── RISING WEBSITE PANEL ── */}
      <div
        ref={siteRef}
        className={`site-panel ${isTransitioning ? "rise-up" : ""}`}
      >
        {/* This panel visually represents the website rising from below.
            Your actual <App /> content sits behind this splash overlay at
            z-index 0. This panel is just the "floor" lift effect.
            Remove this div if your app already renders below. */}
      </div>
    </>
  );
};

export default Welcome;